import {
  isAxiosError,
  type AxiosError,
  type AxiosInstance,
  type InternalAxiosRequestConfig,
} from "axios";
import i18n from "i18next";

import {
  logoutToLogin,
  MissingRefreshTokenError,
  refreshAccessToken,
} from "@/features/auth/token-refresh";
import StorageManager from "@/features/storage";

import { type NmibleAxiosRequestConfig } from "./axios-instance-v3";

const UNPROTECTED_ROUTES: string[] = [
  "/participants/auth/onboard",
  "/participants/auth/login",
  "/auth/refresh",
];

const getSentAccessToken = (config: InternalAxiosRequestConfig) => {
  const header = config.headers?.Authorization;
  return typeof header === "string" ? header.replace("Bearer ", "") : null;
};

/** A rejection means the server refused the token; a network blip is not a logout. */
const isRefreshRejection = (refreshError: unknown) =>
  refreshError instanceof MissingRefreshTokenError ||
  (isAxiosError(refreshError) && refreshError.response != null);

export const setupInterceptors = (instance: AxiosInstance) => {
  instance.interceptors.request.use(
    (config) => {
      const token = StorageManager.getTokenData()?.access_token;
      const pathname = config.url;
      if (token && pathname && !UNPROTECTED_ROUTES.includes(pathname)) {
        config.headers.Authorization = `Bearer ${token}`;
      }

      // Add the current language to Accept-Language header
      const currentLanguage = i18n.language;
      config.headers["Accept-Language"] = currentLanguage.toLowerCase();

      return config;
    },
    (error: AxiosError) => {
      return Promise.reject(error);
    }
  );

  instance.interceptors.response.use(
    (response) => {
      return response;
    },

    async (error: AxiosError<{ err_code: number }>) => {
      const config = error.config as
        | (InternalAxiosRequestConfig & NmibleAxiosRequestConfig)
        | undefined;
      const response = error.response;
      const isAuthEndpoint = UNPROTECTED_ROUTES.includes(config?.url ?? "");

      if (!response) {
        // Network error: nothing auth-related to handle.
        return Promise.reject(error);
      }

      if (
        response.status === 401 &&
        config &&
        !config._retried &&
        !isAuthEndpoint
      ) {
        try {
          const storedToken = StorageManager.getTokenData()?.access_token;
          const rotatedByAnotherTab =
            storedToken != null && storedToken !== getSentAccessToken(config);
          if (!rotatedByAnotherTab) {
            await refreshAccessToken();
          }

          config._retried = true;
          // The request interceptor re-reads the freshly stored access token.
          return instance.request(config);
        } catch (refreshError) {
          if (isRefreshRejection(refreshError)) {
            logoutToLogin();
            response.status = response.data.err_code;
          }
          return Promise.reject(error);
        }
      }

      if (
        response.status === 401 ||
        (response.status === 403 && !config?.skipAuthErrorLogout)
      ) {
        if (isAuthEndpoint) {
          // Failed login/onboard or rejected refresh: the caller handles the
          // error inline (or already navigates via the branch above).
          StorageManager.removeToken();
        } else {
          logoutToLogin();
        }

        response.status = response.data.err_code;
      }

      return Promise.reject(error);
    }
  );
};
